import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Droplets, Sparkles, Wand2, Infinity as InfinityIcon, ArrowRight } from "lucide-react";
import { IconCoin } from "@/components/ui/cartoon-icons";
import { NewUserOfferStrip } from "@/components/billing/new-user-offer-strip";
import { useNewUserOffer } from "@/hooks/use-new-user-offer";

interface UpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Cost of the generation the user just tried to run. */
  creditCost: number;
  /** The user's current balance. */
  credits: number;
}

const PERKS = [
  { Icon: Droplets, title: "Remove watermarks", desc: "Clean, share-ready downloads" },
  { Icon: Sparkles, title: "720p HD quality", desc: "Sharper, higher-res videos" },
  { Icon: Wand2, title: "Every style & tool", desc: "Videos, characters & edits" },
  { Icon: InfinityIcon, title: "Tokens never expire", desc: "Use them whenever you like" },
];

/**
 * Out-of-credits moment. Shows what a pack unlocks and sends the user to the
 * billing page to choose one. Conversion analytics (Add to Cart) fires on the
 * billing page itself, not here.
 */
export function UpsellModal({ isOpen, onClose, creditCost, credits }: UpsellModalProps) {
  const { isActive: offerActive, countdownLabel } = useNewUserOffer({ startTimer: isOpen });

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="fixed inset-0 bg-[#241d3a]/35 backdrop-blur-md" onClick={onClose} style={{ zIndex: 0 }} />
      <div
        className="relative rounded-3xl max-w-sm w-full mx-4 overflow-hidden border-2 border-[#2a2342] shadow-[5px_5px_0_rgba(42,35,66,0.18)] animate-modal-in"
        style={{ zIndex: 1, background: "linear-gradient(170deg, #ffffff 0%, #f6f2ff 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-6 pt-7 pb-4 text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[100px] bg-[#b8a5f0]/25 rounded-full blur-[60px] pointer-events-none" />
          <div className="relative">
            <div className="size-14 rounded-2xl bg-[#b8a5f0]/20 border border-[#b8a5f0]/40 flex items-center justify-center mx-auto mb-3">
              <IconCoin className="size-7" />
            </div>
            <h3 className="text-xl font-extrabold text-[#241d3a] mb-1">You're out of tokens</h3>
            <p className="text-[13px] text-[#241d3a]/55 leading-relaxed">
              This costs <strong className="text-[#241d3a]">{creditCost}</strong> tokens and you have{" "}
              <strong className="text-[#241d3a]">{credits}</strong>. Grab a pack to keep creating.
            </p>
          </div>
        </div>

        <NewUserOfferStrip isActive={offerActive} countdownLabel={countdownLabel} />

        <div className="px-5 pb-5 flex flex-col gap-2.5">
          {PERKS.map(({ Icon, title, desc }) => (
            <div key={title} className="flex items-center gap-3 rounded-2xl border border-[#241d3a]/10 bg-[#241d3a]/[0.03] px-3.5 py-2.5">
              <div className="size-9 shrink-0 rounded-xl bg-[#b8a5f0]/20 border border-[#b8a5f0]/30 flex items-center justify-center">
                <Icon className="size-[18px] text-[#7c5cf0]" strokeWidth={2.4} />
              </div>
              <div className="min-w-0">
                <p className="text-[13px] font-extrabold text-[#241d3a] leading-tight">{title}</p>
                <p className="text-[11px] text-[#241d3a]/50 leading-tight">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="px-5 pb-6 flex flex-col gap-2.5">
          <Link
            to="/billing"
            onClick={onClose}
            className={
              offerActive
                ? "w-full py-3.5 rounded-2xl bg-emerald-500 text-white text-[15px] font-extrabold border-2 border-[#2a2342] shadow-[3px_3px_0_rgba(42,35,66,0.2)] hover:bg-emerald-600 transition-all flex items-center justify-center gap-2"
                : "btn-accent w-full py-3.5 rounded-2xl text-[15px] flex items-center justify-center gap-2"
            }
          >
            {offerActive ? "Claim bonus packs" : "View Packs"}
            <ArrowRight className="size-4" />
          </Link>
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl text-[12.5px] font-semibold text-[#241d3a]/50 hover:text-[#241d3a] transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
